import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Plus, Gift, Calendar, Heart, Cake, Trash2, CalendarPlus } from 'lucide-react';
import useDemoDates from '@/hooks/useDemoDates';
import AddDateDialog from './AddDateDialog';
import { AddToCalendarButton } from '@/components/native/ContactActionButtons';

const BirthdaysTab = () => {
  const { dates, loading, addDate, deleteDate } = useDemoDates('birthday');
  const [dialogOpen, setDialogOpen] = useState(false);

  const handleDelete = async (id) => {
    if (!window.confirm('Remove this birthday? You can always add it again later.')) return;
    await deleteDate(id);
  };

  const handleDateAdded = async (dateData) => {
    await addDate(dateData);
    setDialogOpen(false);
  };

  const getDaysUntil = (dateStr) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const [year, month, day] = dateStr.split('-').map(Number);
    
    // Get this year's occurrence
    let nextDate = new Date(today.getFullYear(), month - 1, day);
    if (nextDate < today) {
      nextDate = new Date(today.getFullYear() + 1, month - 1, day);
    }
    
    const diffTime = nextDate - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  if (loading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-32 w-full" />
      </div>
    );
  }

  return (
    <>
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-6">
        <div className="min-w-0">
          <h3 className="text-lg font-semibold text-gray-900">Birthdays & Anniversaries</h3>
          <p className="text-sm text-gray-500">Never miss a celebration</p>
        </div>
        <Button
          onClick={() => setDialogOpen(true)}
          className="bg-[#C5D9ED] hover:bg-[#B0CCE3] text-gray-900 whitespace-nowrap flex-shrink-0"
          data-testid="add-birthday-btn"
        >
          <Plus className="h-4 w-4 mr-2" />
          Add Birthday
        </Button>
      </div>

      {dates.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-lg border-2 border-dashed border-gray-200">
          <Cake className="h-12 w-12 mx-auto text-[#C5D9ED] mb-4" />
          <h4 className="text-lg font-medium text-gray-900 mb-2">Ready to Track Birthdays</h4>
          <p className="text-gray-600 mb-4">
            Add birthdays and anniversaries with gift ideas so you never miss a celebration
          </p>
          <Button
            onClick={() => setDialogOpen(true)}
            className="bg-[#C5D9ED] hover:bg-[#B0CCE3] text-gray-900"
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Your First Birthday
          </Button>
        </div>
      ) : (
        <div className="space-y-4">
          {dates.map((item) => {
            const daysUntil = getDaysUntil(item.date);
            const isUpcoming = daysUntil <= 14;
            
            return (
              <Card key={item.id} className={`border-l-4 ${isUpcoming ? 'border-l-pink-400' : 'border-l-[#C5D9ED]'}`}>
                <CardContent className="p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 flex-wrap mb-2">
                        <h4 className="font-medium text-gray-900">
                          {item.person_names && item.person_names.length > 0 
                            ? item.person_names.join(' & ') 
                            : item.title}
                        </h4>
                        {item.relationship && (
                          <Badge variant="secondary" className="bg-pink-100 text-pink-700">
                            <Heart className="h-3 w-3 mr-1" />
                            {item.relationship}
                          </Badge>
                        )}
                        {isUpcoming && (
                          <Badge className="bg-pink-500 text-white">
                            {daysUntil === 0 ? 'Today!' : daysUntil === 1 ? 'Tomorrow!' : `${daysUntil} days`}
                          </Badge>
                        )}
                      </div>
                      
                      <div className="space-y-1 text-sm text-gray-600">
                        <div className="flex items-center gap-1">
                          <Calendar className="h-3.5 w-3.5" />
                          {new Date(item.date + 'T00:00:00').toLocaleDateString('en-US', { 
                            month: 'long', day: 'numeric' 
                          })}
                          {item.repeat_frequency === 'yearly' && ' (yearly)'}
                        </div>
                        
                        {/* Gift Planning Section */}
                        {(item.gift_ideas || item.gift_what_they_want) && (
                          <div className="mt-2 p-2 bg-yellow-50 rounded border border-yellow-200">
                            <div className="flex items-center gap-1 text-yellow-700 font-medium mb-1">
                              <Gift className="h-3.5 w-3.5" />
                              Gift Ideas
                            </div>
                            {item.gift_what_they_want && (
                              <p className="text-sm text-yellow-800">They want: {item.gift_what_they_want}</p>
                            )}
                            {item.gift_ideas && (
                              <p className="text-sm text-yellow-700">{item.gift_ideas}</p>
                            )}
                            {item.gift_where_to_buy && (
                              <p className="text-sm text-yellow-600">Where: {item.gift_where_to_buy}</p>
                            )}
                          </div>
                        )}
                        
                        {/* Sizes */}
                        {(item.size_clothing || item.size_shoe || item.size_ring) && (
                          <div className="flex flex-wrap gap-2 mt-2">
                            {item.size_clothing && (
                              <Badge variant="outline">Clothing: {item.size_clothing}</Badge>
                            )}
                            {item.size_shoe && (
                              <Badge variant="outline">Shoe: {item.size_shoe}</Badge>
                            )}
                            {item.size_ring && (
                              <Badge variant="outline">Ring: {item.size_ring}</Badge>
                            )}
                          </div>
                        )}
                        
                        {/* Preferences */}
                        {item.preferences && (
                          <p className="text-gray-500 italic">Preferences: {item.preferences}</p>
                        )}
                        
                        {/* Offered to provide */}
                        {item.offered_to_provide && (
                          <div className="mt-2 p-2 bg-blue-50 rounded text-blue-800">
                            <strong>You offered:</strong> {item.offered_to_provide}
                          </div>
                        )}
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-2">
                      <AddToCalendarButton
                        dateRecord={{
                          title: item.person_names && item.person_names.length > 0 
                            ? `${item.person_names.join(' & ')}'s Birthday`
                            : item.title,
                          date: item.date,
                          type: 'birthday',
                          recurrence: 'yearly',
                          all_day: true,
                          reminder_minutes: 1440, // 1 day before
                          notes: item.gift_ideas || item.preferences || ''
                        }}
                        size="sm"
                        variant="ghost"
                        className="text-purple-600 hover:text-purple-700 hover:bg-purple-50"
                        data-testid={`add-to-calendar-${item.id}`}
                      />
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDelete(item.id)}
                        className="text-red-500 hover:text-red-700 hover:bg-red-50"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      <AddDateDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        category="birthday"
        onSuccess={handleDateAdded}
      />
    </>
  );
};

export default BirthdaysTab;
